import { FC, useState, useEffect, ReactNode } from "react";
import { getRuntimeConfig } from "@/utils/runtimeConfig";
import { useTranslation } from "react-i18next";
import { Contract, ContractAttachment, ContractLightSearch, ContractTradeType, CreateApproval } from "@/types";
import {
  contractsUIConfig,
  getContractCardDetails,
  getContractParties,
  getContractProgressPercent,
  getContractTradeType,
} from "@/utils/Contracts";
import {
  postApproveContract,
  prefetchContractById,
  updateContract,
  useGetContractAttachments,
  useGetContractById,
} from "@/services/Contracts";
import { useDebouncedPrefetch } from "@/hooks/useDebouncedPrefetch";
import { useGetCreateOrderValues } from "@/services/Orders";
import { ApprovalWarningText } from "../../organisms/ContractCard";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { EditContractFields, EditContractInput, EditContractSchema } from "./validation/editContract";
import { ContractToEditContractForm, EditContractFormToCreateContract } from "./helpers/editContractHelpers";
import { stripTime } from "@/utils/Dates";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Grid, UnstyledButton } from "@mantine/core";
import { useAuth } from "@/contexts/AuthContext";
import { getUserContextIds } from "@/utils/sessionStorage";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj } from "@/utils/NotificationUtil";
import { useOpenCreateOrder } from "@/hooks/useGuardedWizards";
import { useTradeLock } from "@/hooks/useTradeLock";
import dayjs from "dayjs";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Group from "@/components/atoms/Group";
import Text from "@/components/atoms/Text";
import styles from "./styles.module.css";
import Heading from "@/components/atoms/Heading";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import Progress from "@/components/atoms/Progress";
import Stack from "@/components/atoms/Stack";
import CTA from "@/components/atoms/CTA";
import ContractStatusEnum from "@/constants/enums/ContractStatusEnum";
import Spinner from "@/components/atoms/Spinner/Spinner";
import Stepper from "../../molecules/Stepper";
import Settings from "./Steps/Settings";
import Invoicing from "./Steps/Invoicing";
import Transport from "./Steps/Transport";
import Attachments from "./Steps/Attachments";
import ContractsTable from "./orderTable/ContractsTable";
import Alert from "@/components/atoms/Alert";
import Deviations from "./Steps/Deviations";
import TrackingChart from "./TrackingChart/TrackingChart";
import DrawerTabs from "@/components/atoms/DrawerTabs";
import ArchiveContractButton from "@/components/molecules/ArchiveContractButton/ArchiveContractButton";
import RenewContractButton from "@/components/molecules/RenewContractButton/RenewContractButton";
import WasteStream from "./WasteStream";
import Details from "./Steps/Details";

export default function ContractDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { currentCompany, user } = useAuth();
  const { guardTradeLock } = useTradeLock();
  const queryClient = useQueryClient();
  const { contractId } = useParams<{ contractId: string }>();

  const {
    data: contract,
    isError: isErrorFetchingContract,
    isLoading: isLoadingContract,
    isFetching: isFetchingContract,
  } = useGetContractById(Number(contractId), { enabled: !!contractId });

  useEffect(() => {
    if (isErrorFetchingContract) {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.fetchError"), t("notifications:contracts.fetchError"))
      );
    }
  }, [isErrorFetchingContract]);

  const { mutate: mutateApproveContract, isPending: isPendingMutateApproval } = useMutation({
    mutationFn: async (contractId: number) => {
      const approvalBody: CreateApproval = {
        approvalDate: stripTime(dayjs().toDate()),
        approvedBy: user?.id as number,
        approvalCompany: currentCompany?.companyId as number,
        contractId: contractId,
      };
      await postApproveContract(contractId, approvalBody);
    },
    onMutate: async (contractId: number) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ["getContractById", contractId] }),
        queryClient.cancelQueries({ queryKey: ["getContractsLight"] }),
      ]);
      const detailSnapshot = queryClient.getQueryData<Contract>(["getContractById", contractId]);
      const listSnapshots = queryClient.getQueriesData<ContractLightSearch>({ queryKey: ["getContractsLight"] });
      const optimisticApproval = {
        id: -1,
        approvalDate: stripTime(dayjs().toDate()).toISOString(),
        approvedBy: user?.id as number,
        approvalCompany: currentCompany?.companyId as number,
        contractId,
      };
      queryClient.setQueryData<Contract>(["getContractById", contractId], (old) =>
        old ? { ...old, approvals: [...old.approvals, optimisticApproval] } : old
      );
      queryClient.setQueriesData<ContractLightSearch>({ queryKey: ["getContractsLight"] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          contracts: old.contracts.map((c) =>
            c.id === contractId ? { ...c, approvals: [...c.approvals, optimisticApproval] } : c
          ),
        };
      });
      return { detailSnapshot, listSnapshots };
    },
    onError: (_err, contractId, context) => {
      if (context?.detailSnapshot) queryClient.setQueryData(["getContractById", contractId], context.detailSnapshot);
      context?.listSnapshots?.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
    },
    onSettled: (_data, _err, contractId) => {
      queryClient.invalidateQueries({ queryKey: ["getContractsLight"] });
      queryClient.invalidateQueries({ queryKey: ["getContractById", contractId] });
    },
  });

  if (!contract) return <Spinner loading={isLoadingContract} />;
  return (
    <>
      <div className={styles.editContractRoot}>
        <div className={styles.editContractInner}>
          <Group align="center">
            <SVGAsset style={{ cursor: "pointer" }} onClick={() => navigate("/contracts")} name="arrow-left" />
            <Text size="md" display="technical" tt="uppercase" strong={true} data-testid={"contract-details-header"}>
              {t("contracts:activeContracts")}
            </Text>
          </Group>
          <EditContractHeader contract={contract} />
          <EditContractForm
            contract={contract}
            approveContract={guardTradeLock(
              () => mutateApproveContract(contract.id),
              () => getContractParties(contract)
            )}
            isPendingMutateApproval={isPendingMutateApproval}
          />
        </div>
      </div>

      <DrawerTabs defaultValue={"orders"} keepMounted={false} className={styles.tabsRoot}>
        <DrawerTabs.List>
          <DrawerTabs.Tab value="orders">
            <Text size="md" display="technical" tt="uppercase" strong={true}>
              {t("common:orders")}
            </Text>
          </DrawerTabs.Tab>
          <DrawerTabs.Tab value="wasteStream">
            <Text size="md" display="technical" tt="uppercase" strong={true}>
              {t("settings:cameras.wasteStream")}
            </Text>
          </DrawerTabs.Tab>
          {getRuntimeConfig("APACHE_VISIBILITY") === "true" && (
            <>
              <DrawerTabs.Tab value="tracking">
                <Text size="md" display="technical" tt="uppercase" strong={true}>
                  {t("common:tracking")}
                </Text>
              </DrawerTabs.Tab>
            </>
          )}
        </DrawerTabs.List>

        <DrawerTabs.Panel value="orders">
          <div className={styles.contractsTable}>
            <header className={styles.contractsTableHeader}>
              <Group>
                <Heading order={5} strong={true}>
                  {contract.buyOrdersOnContract.length + contract.salesOrdersOnContract.length}{" "}
                  {t("contracts:ordersOnContract")}
                </Heading>
                <Spinner inline loading={isFetchingContract} />
              </Group>
            </header>
            <ContractsTable contract={contract} />
          </div>
        </DrawerTabs.Panel>

        <DrawerTabs.Panel value="wasteStream">
          <WasteStream contractId={contract.id} companyId={currentCompany?.companyId as number} />
        </DrawerTabs.Panel>

        {getRuntimeConfig("APACHE_VISIBILITY") === "true" && (
          <>
            <DrawerTabs.Panel value="tracking">
              <div className={styles.trackingChartContainer}>
                <TrackingChart
                  currentCompanyId={currentCompany?.companyId}
                  companySpecificProductId={contract.contractProduct.companySpecificProduct.id}
                />
              </div>
            </DrawerTabs.Panel>
          </>
        )}
      </DrawerTabs>
    </>
  );
}

interface EditContractFormProps {
  contract: Contract;
  approveContract: () => void;
  isPendingMutateApproval: boolean;
}

const EditContractForm: FC<EditContractFormProps> = ({ contract, approveContract, isPendingMutateApproval }) => {
  const { t } = useTranslation();
  const userIds = getUserContextIds();

  const { currentCompany, isViewer } = useAuth();
  const { explainTradeLockError } = useTradeLock();
  const queryClient = useQueryClient();

  const { data: createOrderValues } = useGetCreateOrderValues();

  const [newOrExistingProduct, setNewOrExistingProduct] = useState<"new" | "saved">("saved");
  const [contractAttachments, setContractAttachments] = useState<ContractAttachment[]>([]);
  const [currentStep, setCurrentStep] = useState(1);

  const { data: attachments, isLoading } = useGetContractAttachments(contract.id);
  console.log(attachments);

  const open = useOpenCreateOrder();

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (!isLoading && attachments.length > 0) {
      setContractAttachments(attachments);
    }
  }, [attachments, isLoading]);

  const { mutate: mutateUpdateContract } = useMutation({
    mutationFn: async (data: EditContractInput) => {
      const contractId = contract.id;
      const editedContract = EditContractFormToCreateContract(data);
      editedContract.attachments = []; // does nothing, attachment logic has its own endpoint;

      await updateContract(editedContract, contractId, userIds.companyId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getContractById", contract.id],
      });
    },
    onError: (error) => {
      if (explainTradeLockError(error, getContractParties(contract))) {
        // The cached contract still shows the partner as unlocked; refetched, approving is refused up front.
        queryClient.invalidateQueries({ queryKey: ["getContractById", contract.id] });
        return;
      }
      notifications.show(ErrorNotificationObj(t("notifications:generic.saveError"), ""));
    },
  });

  const handleApproveContract = () => {
    approveContract();
  };

  const disableForm =
    contract.contractStatus !== ContractStatusEnum.PENDING_APPROVAL ||
    contract.approvals.some((approval) => approval.approvalCompany === currentCompany?.companyId) ||
    isViewer(currentCompany) ||
    contract.createdByCompany !== currentCompany?.companyId;

  const editContractForm = useForm<EditContractInput>({
    resolver: zodResolver(EditContractSchema),
    defaultValues: ContractToEditContractForm(contract) as unknown as EditContractInput,
    shouldUnregister: false,
  });

  /*   const newProductForm = useForm<NewProductInput>({
    resolver: zodResolver(NewProductSchema),
    disabled:
      isViewer(user, currentCompany) ||
      newOrExistingProduct === "saved" ||
      contract.contractStatus !== ContractStatusEnum.PENDING_APPROVAL ||
      contract.createdByCompany !== currentCompany.companyId,
  });
 */
  async function onSubmit(data: any) {
    if (newOrExistingProduct === "new") {
      notifications.show(
        ErrorNotificationObj(t("notifications.generic.error"), t("validation.unfinishedProductCreation"))
      );
    } else {
      mutateUpdateContract(data);
    }
  }

  type StepConfig = {
    label: string;
    content: ReactNode;
    fieldsThatTriggerStepError: Array<EditContractFields>;
  };

  const steps: StepConfig[] = [
    {
      label: t("common:details"),
      fieldsThatTriggerStepError: [],
      content: (
        <Details
          contract={contract}
          newOrExistingProductState={newOrExistingProduct}
          onChangeNewOrExistingProduct={setNewOrExistingProduct}
          disableForm={disableForm}
          editContractForm={editContractForm}
          mutateUpdateContract={mutateUpdateContract}
          setNewOrExistingProduct={setNewOrExistingProduct}
        />
      ),
    },
    {
      label: t("common:settings"),
      fieldsThatTriggerStepError: ["amount", "contractEndDate", "name"],
      content: (
        <Settings
          editContractForm={editContractForm}
          disableForm={disableForm}
          contract={contract}
          createOrderValues={createOrderValues}
        />
      ),
    },
    {
      label: t("contracts:invoicing"),
      fieldsThatTriggerStepError: ["agreedBuyerPricePrUnit", "agreedSellerPricePrUnit"],
      content: (
        <Invoicing
          contract={contract}
          disableForm={disableForm}
          editContractForm={editContractForm}
          createOrderValues={createOrderValues}
        />
      ),
    },
    {
      label: t("common:deviations"),
      fieldsThatTriggerStepError: [],
      content: <Deviations contract={contract} editContractForm={editContractForm} disableForm={disableForm} />,
    },
    {
      label: t("contracts:transport"),
      fieldsThatTriggerStepError: ["transportResponsible"],
      content: <Transport contract={contract} disableForm={disableForm} editContractForm={editContractForm} />,
    },
    {
      label: t("contracts:attachments"),
      fieldsThatTriggerStepError: [],
      content: (
        <Attachments
          files={contractAttachments}
          disableForm={disableForm}
          setFiles={setContractAttachments}
          editContractForm={editContractForm}
          contract={contract}
        />
      ),
    },
  ];
  return (
    <>
      <Stepper
        active={currentStep}
        onStepClick={setCurrentStep}
        classNames={{
          stepIcon: styles.stepIcon,
          separator: styles.separator,
          steps: styles.stepperHeader,
          stepLabel: styles.stepLabel,
          content: styles.stepperContent,
        }}
      >
        {steps.map((step, index) => {
          let contractHasErrors = false;
          let newProductHasErrors = false;
          contractHasErrors = step.fieldsThatTriggerStepError.some(
            (f) => !!(editContractForm.formState.errors as any)[f]
          );
          if (newOrExistingProduct === "new" && index === 0) newProductHasErrors = true;
          return (
            <Stepper.Step
              key={"contractdetails" + index}
              data-testid={`contract-details-step-${index}`}
              label={step.label}
              completedIcon={index + 1}
              classNames={{
                stepIcon: contractHasErrors || newProductHasErrors ? styles.stepIconError : undefined,
              }}
            >
              {step.content}
            </Stepper.Step>
          );
        })}
      </Stepper>
      <Group justify="center" className={styles.stepNavigationButtons}>
        <UnstyledButton variant="default" onClick={() => currentStep > 0 && setCurrentStep(currentStep - 1)}>
          <Text size="sm" display="technical">
            {currentStep > 0 ? `<  ${steps[currentStep - 1].label}` : ""}
          </Text>
        </UnstyledButton>
        <UnstyledButton onClick={() => currentStep < steps.length - 1 && setCurrentStep(currentStep + 1)}>
          <Text size="sm" display="technical">
            {currentStep < steps.length - 1 ? steps[currentStep + 1].label + " >" : ""}
          </Text>
        </UnstyledButton>
      </Group>

      <footer className={styles.editContractFooter}>
        <Grid>
          <Grid.Col span={6}>
            {contract.tradeAtOwnRisk && (
              <Alert color="warning" title={t("contracts:creditWarningTitle")}>
                {t("contracts:creditWarningDetailsPage")}
              </Alert>
            )}
          </Grid.Col>

          <Grid.Col span={6}>
            <Group justify="flex-end" gap="10px">
              {contract.createdByCompany === currentCompany?.companyId && (
                <>
                  <RenewContractButton contract={contract} />
                  <ArchiveContractButton contract={contract} />
                </>
              )}
              {contract.contractStatus === ContractStatusEnum.PENDING_APPROVAL && (
                <CTA
                  size="sm"
                  onClick={handleApproveContract}
                  data-testid="approve-contract-btn"
                  intent="affirm"
                  type="submit"
                  tt="uppercase"
                  loading={isPendingMutateApproval}
                  disabled={
                    !!contract.approvals.find((approval) => approval.approvalCompany === userIds.companyId) ||
                    isViewer(currentCompany)
                  }
                >
                  {t("contracts:approveContract")}
                </CTA>
              )}
              {contract.contractStatus === ContractStatusEnum.PENDING_APPROVAL &&
                contract.createdByCompany === currentCompany?.companyId && (
                  <CTA
                    size="sm"
                    onClick={editContractForm.handleSubmit(onSubmit)}
                    data-testid="update-contract-btn"
                    loading={isPendingMutateApproval}
                    disabled={
                      !!contract.approvals.find((approval) => approval.approvalCompany === userIds.companyId) ||
                      isViewer(currentCompany)
                    }
                    intent="affirm"
                    type="submit"
                    tt="uppercase"
                  >
                    {t("actions:updateContract")}
                  </CTA>
                )}
              <CTA
                size="sm"
                data-testid="new-order-btn"
                disabled={contract.contractStatus !== ContractStatusEnum.ACTIVE || isViewer(currentCompany)}
                onClick={() => open(contract, "contract")}
                intent="primary"
                tt="uppercase"
              >
                {t("actions:newOrder")}
              </CTA>
            </Group>
          </Grid.Col>
        </Grid>
      </footer>
    </>
  );
};

interface EditContractHeaderProps {
  contract: Contract;
}

const EditContractHeader: FC<EditContractHeaderProps> = ({ contract }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const schedulePrefetch = useDebouncedPrefetch();

  const { currentCompany } = useAuth();
  const contractTradeType = getContractTradeType(contract, currentCompany);
  const { iconName, companyIcon, partnerIcon } = contractsUIConfig(contractTradeType);
  const { sellerName, buyerName, createdDate, notApproved } = getContractCardDetails(contract);

  return (
    <header className={styles.editContractHeader}>
      <Group justify="space-between" gap="20px" align="flex-start">
        <Group className={styles.contractStatus} justify="flex-start" align="flex-start">
          <SVGAsset name={iconName} width="36" height="36" />
          <Stack flex={1}>
            <Heading data-testid={"contract-header-name"} order={4} styleOrder={5} strong={true}>
              {contract.name}
            </Heading>
            {contract.contractStatus !== ContractStatusEnum.PENDING_APPROVAL ? (
              <ProgressWithText
                percent={getContractProgressPercent(contract)}
                contractTradeType={contractTradeType}
                t={t}
              />
            ) : (
              <ApprovalWarningText notApproved={notApproved} contract={contract} />
            )}
            {contract.previousContractId && (
              <Link
                to={"/contracts/details/" + contract.previousContractId}
                onMouseEnter={() =>
                  schedulePrefetch(() => prefetchContractById(queryClient, contract.previousContractId!))
                }
              >
                <Text size="sm" color="primary" data-testid={"go-to-previous"}>
                  {t("contracts:goToPreviousContract")}
                </Text>
              </Link>
            )}
          </Stack>
        </Group>
        <div className={styles.contractPartners}>
          <Text size="xs" className={styles.dateCreated} color="secondary">
            {createdDate && t("common:created") + " " + dayjs(createdDate).local().format("DD.MM.YYYY")}
          </Text>
          <Group gap="20px" justify="flex-end" className={`${styles.traders} ${styles[contractTradeType]}`}>
            <Text size="lg" strong={true}>
              <SVGAsset name={companyIcon} width="18" height="18" className={styles.tradeIcon} />
              {contractTradeType === ContractTradeTypeEnum.SELL ? sellerName : buyerName}
            </Text>
            <Text size="lg" strong={true}>
              <SVGAsset name={partnerIcon} width="18" height="18" className={styles.tradeIcon} />
              {contractTradeType === ContractTradeTypeEnum.SELL ? buyerName : sellerName}
            </Text>
          </Group>
        </div>
      </Group>
    </header>
  );
};

interface ProgressWithTextProps {
  contractTradeType: ContractTradeType;
  percent: number;
  t: any;
}

const ProgressWithText: FC<ProgressWithTextProps> = ({ contractTradeType, percent, t }) => (
  <Stack align="flex-end">
    <Text
      size="xs"
      color="secondary"
      data-testid={"progress-with-text"}
      style={{ position: "absolute", marginTop: "-24px" }}
    >{`${percent}% ${t("common:completed").toLowerCase()}`}</Text>
    <Progress w="100%" value={percent} contractTradeType={contractTradeType} />
  </Stack>
);
